// Platform display/capability metadata, served from the backend PlatformSpec
// registry via GET /v1/platforms. Replaces the hand-maintained
// PLATFORMS list, label/colour maps, PDP-URL switch, and tier vocabulary that used to
// live in labels.ts / tiers.tsx / Proxies.tsx.
//
// The query is fetched once at boot by <PlatformBoot> and pinned (staleTime: Infinity)
// so it never silently changes under the synchronous accessors below (which are NOT
// reactive — a component calling platformLabel() won't re-render on a refetch, and we
// don't want it to for static config). CACHE is filled in an effect, never during render.

import { useListPlatformsV1PlatformsGet } from "./generated/qcsControlPlane";
import { Flow, Platform, type PlatformMeta, type TierMeta } from "./generated/model";

let CACHE: PlatformMeta[] = [];

const PLATFORM_KEYS: ReadonlySet<string> = new Set(Object.values(Platform));
/** Narrow a key that arrived as text (a route param, a select value, a CSV cell) to the
 * API's closed set. The API rejects an unknown key with 422, so a caller checks here and
 * renders its own "unknown platform" state instead of sending the request. */
export const isPlatform = (key: string): key is Platform => PLATFORM_KEYS.has(key);
/** A key from a URL (a route param, a query string) as a platform, or `undefined` when it
 * is not exactly one of the API's keys: the API refuses `Blinkit` with 422, so the page
 * renders its own "unknown platform" state instead of a repaired guess. */
export const platformFromKey = (raw: string): Platform | undefined =>
  isPlatform(raw) ? raw : undefined;

const titleCase = (k: string) => (k ? k[0].toUpperCase() + k.slice(1) : k);
const find = (k: string) => CACHE.find((p) => p.key === k);

/** Fetch the platform contract once and keep the module cache populated (call at boot).
 *
 * CACHE is assigned during render (not in an effect) so a synchronous reader rendered in
 * the same commit — e.g. <PlatformStyles> in <PlatformBoot> — sees the populated data. It
 * is idempotent, and the boot gate blocks children until the first fetch resolves, so no
 * reader ever runs before CACHE is filled. staleTime: Infinity pins the data so the
 * non-reactive accessors below never observe it change under them. */
export function usePlatforms() {
  const q = useListPlatformsV1PlatformsGet({ query: { staleTime: Number.POSITIVE_INFINITY } });
  if (q.data) CACHE = q.data;
  return q;
}

export const platforms = (): PlatformMeta[] => CACHE;
export const platformKeys = (): Platform[] => CACHE.map((p) => p.key);
export const platformSupports = (k: string, flow: Flow): boolean => {
  const platform = find(k);
  if (!platform) return false;
  // Taxonomy and SKU match are platform-agnostic passes over the SKU dimension: they
  // have no adapter, so they are deliberately absent from the capability map and must
  // not be gated on it. SKU match is more than agnostic - it reads every platform at
  // once, since a match is a group of listings from different ones.
  return (
    flow === Flow.taxonomy_classify ||
    flow === Flow.product_match ||
    platform.capabilities[flow] === true
  );
};
/** The platforms that can run ``flow``, as ONE array per flow.
 *
 * Held rather than rebuilt, because callers put the result straight into a `useMemo`
 * dependency list: a fresh array each call makes every such memo miss on every render, so
 * a page like Rank rebuilds its whole TanStack column set on each keystroke in its filter
 * box. Keyed on the CACHE array itself, so the moment `usePlatforms` assigns a new one the
 * held list is rebuilt -- no reader can be handed a stale capability set. Callers only
 * read the array; none sorts or splices it in place. */
const KEYS_BY_FLOW = new Map<Flow, { cache: PlatformMeta[]; keys: Platform[] }>();
export const platformKeysFor = (flow: Flow): Platform[] => {
  const held = KEYS_BY_FLOW.get(flow);
  if (held && held.cache === CACHE) return held.keys;
  const keys = CACHE.filter((p) => platformSupports(p.key, flow)).map((p) => p.key);
  KEYS_BY_FLOW.set(flow, { cache: CACHE, keys });
  return keys;
};
export const platformLabel = (k: string): string => find(k)?.label ?? titleCase(k);
/** The platform's brand colour (a concrete hex, usable for map markers), or a neutral blue. */
export const platformColor = (k: string): string => find(k)?.color ?? "#1f6feb";
/** Registry-provided marker/logo asset, with a PNG fallback for pre-registry boot states. */
export const platformLogo = (k: string): string =>
  find(k)?.logo_url ?? `/platform-logos/${k}.png`;
/** True when the platform has a multi-lane delivery model (lane pills vs a single role pill). */
export const platformHasLanes = (k: string): boolean => find(k)?.has_lanes ?? false;
export const platformHasPdpStage = (k: string): boolean => find(k)?.has_pdp_stage ?? false;
export const platformSupportsManualPdp = (k: string): boolean => {
  const platform = find(k);
  return platform?.supports_manual_pdp === true;
};

/** Consumer-facing storefront PDP URL for a SKU, from the platform's pdp_url_template. */
export function pdpUrl(platform: string, productId: string): string {
  const t = find(platform)?.pdp_url_template;
  return t ? t.replace("{id}", encodeURIComponent(productId)) : "#";
}

/** Consumer-web brand SEARCH URL for a term, from brand_web_search_url_template.
 *  Returns null when the platform has no shareable brand URL (no link is shown). */
export function brandSearchUrl(platform: string, term: string): string | null {
  const t = find(platform)?.brand_web_search_url_template;
  return t ? t.replace("{q}", encodeURIComponent(term)) : null;
}

/** The platform's per-cell delivery/service tiers (the LANE axis) for the legend + colours. */
export const tiersFor = (k: string): TierMeta[] => find(k)?.tiers ?? [];
